import "./contactsManager.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';
import { ContactsImporter, ImportedContact } from '@ts/components/ContactsImporter';

export type ContactsManagerOptions = {
    onImport?: (contacts: ImportedContact[]) => void;
    tableSelector?: string;
    rowSelector?: string;
    addRowTriggerSelector?: string;
    launchButtonSelector?: string;
    cloudSpongeOptions?: {};
};

export class ContactsManager {
    private static registry = new ComponentRegistry<ContactsManager>('contacts-manager');
    private readonly id: string;
    private readonly container: HTMLElement;
    private readonly rowSelector: string;
    private readonly rowTemplate: HTMLTableRowElement | null;
    private readonly addRowTrigger: HTMLElement | null;
    private readonly importer: ContactsImporter;

    private constructor(
        container: HTMLElement,
        private options: ContactsManagerOptions = {}
    ) {
        if (!(container instanceof HTMLElement)) {
            throw new Error('ContactsManager: Element must be an HTMLElement');
        }

        this.id = ContactsManager.registry.register(container, this);
        this.container = container;

        this.queryTable();
        this.rowSelector = options.rowSelector ?? '.address-import-row';
        this.rowTemplate = this.captureRowTemplate();
        this.addRowTrigger = this.queryAddRowTrigger();

        this.setAddRowTrigger();
        this.setRemoveRowDelegation();
        this.updateRemoveButtonStates();

        this.importer = new ContactsImporter(container, {
            cloudSpongeOptions: options.cloudSpongeOptions,
            launchButtonSelector: options.launchButtonSelector,
            onImport: contacts => this.importContacts(contacts)
        });
    }

    private queryTable(): { table: HTMLTableElement; tbody: HTMLTableSectionElement } {
        const tableSelector = this.options.tableSelector ?? '.address-import-table';
        const table = this.container.querySelector<HTMLTableElement>(tableSelector);
        const tbody = table?.querySelector<HTMLTableSectionElement>('tbody');
        if (!table || !tbody) {
            throw new Error(`ContactsManager: Missing ${tableSelector} or tbody`);
        }

        return { table, tbody };
    }

    private queryAddRowTrigger(): HTMLElement | null {
        return this.container.querySelector<HTMLElement>(this.options.addRowTriggerSelector ?? '.add-import-row');
    }

    private captureRowTemplate(): HTMLTableRowElement | null {
        // Captured once, then cleared, so clones are always pristine even if the source row had content.
        const { tbody } = this.queryTable();
        const firstRow = tbody.querySelector<HTMLTableRowElement>(this.rowSelector);
        if (!firstRow) return null;

        const template = firstRow.cloneNode(true) as HTMLTableRowElement;
        this.clearRow(template);
        return template;
    }

    private setAddRowTrigger(): void {
        this.addRowTrigger?.addEventListener('click', (e) => {
            e.preventDefault();
            this.addRow();
        });
    }

    private setRemoveRowDelegation(): void {
        this.container.addEventListener('click', (e) => {
            const removeBtn = (e.target as HTMLElement).closest<HTMLButtonElement>('.remove-address-import-row');
            if (!removeBtn) return;

            e.preventDefault();
            const row = removeBtn.closest<HTMLTableRowElement>(this.rowSelector);
            if (row) this.removeRow(row);
        });
    }

    public static create(
        target: string | HTMLElement,
        options: ContactsManagerOptions = {}
    ): ContactsManager | null {
        const elements = ComponentRegistry.processTargets<ContactsManager, HTMLElement>(
            ContactsManager.registry,
            target
        );

        if (!elements?.length) return null;
        return new ContactsManager(elements[0], options);
    }

    public addRow(prefill?: ImportedContact): HTMLTableRowElement | null {
        if (!this.rowTemplate) return null;
        const { tbody } = this.queryTable();

        const row = this.rowTemplate.cloneNode(true) as HTMLTableRowElement;
        if (prefill) this.fillRow(row, prefill);

        tbody.appendChild(row);
        this.reindexRows();
        this.updateRemoveButtonStates();
        return row;
    }

    public removeRow(row: HTMLTableRowElement): void {
        const { tbody } = this.queryTable();
        const rows = tbody.querySelectorAll<HTMLTableRowElement>(this.rowSelector);
        if (rows.length <= 1) return;

        row.remove();
        this.reindexRows();
        this.updateRemoveButtonStates();
    }

    private reindexRows(): void {
        const { tbody } = this.queryTable();
        const rows = tbody.querySelectorAll<HTMLTableRowElement>(this.rowSelector);

        rows.forEach((row, index) => {
            row.querySelectorAll<HTMLInputElement>('input').forEach(input => {
                if (input.name) {
                    input.name = input.name.replace(/Contacts(?:\[\d+\]|_\d+__)/, `Contacts[${index}]`);
                }

                if (input.id) {
                    input.id = input.id.replace(/Contacts_\d+__/, `Contacts_${index}__`);
                }
            });

            row.querySelectorAll<HTMLElement>('[data-valmsg-for]').forEach(validationMessage => {
                const field = validationMessage.dataset.valmsgFor;
                if (field) {
                    validationMessage.dataset.valmsgFor = field.replace(/Contacts(?:\[\d+\]|_\d+__)/, `Contacts[${index}]`);
                }
            });
        });
    }

    public importContacts(contacts: ImportedContact[]): void {
        const { tbody } = this.queryTable();
        const emptyRows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>(this.rowSelector))
            .filter(row => this.isRowEmpty(row));

        contacts.forEach(contact => {
            const targetRow = emptyRows.shift();
            if (targetRow) {
                this.fillRow(targetRow, contact);
            } else {
                this.addRow(contact);
            }
        });

        this.options.onImport?.(contacts);
    }

    private fillRow(row: HTMLTableRowElement, contact: ImportedContact): void {
        const firstName = row.querySelector<HTMLInputElement>('.address-import-first-name');
        const lastName = row.querySelector<HTMLInputElement>('.address-import-last-name');
        const email = row.querySelector<HTMLInputElement>('.address-import-email');

        if (firstName) firstName.value = contact.firstName ?? '';
        if (lastName) lastName.value = contact.lastName ?? '';
        if (email) email.value = contact.email ?? '';
    }

    private clearRow(row: HTMLTableRowElement): void {
        row.querySelectorAll<HTMLInputElement>('input').forEach(input => {
            input.value = '';
            input.classList.remove('input-validation-error');
            input.removeAttribute('aria-invalid');
        });

        row.querySelectorAll<HTMLElement>('[data-valmsg-for]').forEach(message => {
            message.textContent = '';
            message.classList.remove('field-validation-error');
            message.classList.add('field-validation-valid');
        });
    }

    private isRowEmpty(row: HTMLTableRowElement): boolean {
        const inputs = row.querySelectorAll<HTMLInputElement>('.address-import-first-name, .address-import-last-name, .address-import-email');
        return Array.from(inputs).every(input => input.value.trim() === '');
    }

    private updateRemoveButtonStates(): void {
        const { tbody } = this.queryTable();
        const rows = tbody.querySelectorAll<HTMLTableRowElement>(this.rowSelector);
        const disableRemove = rows.length <= 1;

        rows.forEach(row => {
            const removeBtn = row.querySelector<HTMLButtonElement>('.remove-address-import-row');
            if (removeBtn) removeBtn.disabled = disableRemove;
        });
    }

    public static getContactsManager(id: string): ContactsManager | null {
        return ContactsManager.registry.get(id) ?? null;
    }
}
